import { useEffect, useRef, useState } from 'react';

import type React from 'react';

export type ScrollMetrics = { scrollLeft: number; viewportWidth: number };

/**
 * Scroll position and viewport width of the single scroll container, coalesced to one update per
 * frame. This is the only scroll listener in the module: the header and the row headers stay in
 * place with `position: sticky`, so nothing has to be mirrored by hand the way the chart this
 * replaced did, from a `scroll` handler feeding a second header container.
 *
 * The returned value is one frame behind the DOM by construction — a scroll event only schedules
 * the read. Anything that must react to a scroll position it has just written itself (the
 * initial centring, the prepend compensation) has to read the element, not this.
 */
export const useScrollMetrics = (ref: React.RefObject<HTMLElement | null>): ScrollMetrics => {
  const [metrics, setMetrics] = useState<ScrollMetrics>({ scrollLeft: 0, viewportWidth: 0 });
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => {
      frame.current = null;
      setMetrics((current) => {
        const next = { scrollLeft: element.scrollLeft, viewportWidth: element.clientWidth };

        return current.scrollLeft === next.scrollLeft &&
          current.viewportWidth === next.viewportWidth
          ? current
          : next;
      });
    };

    const schedule = () => {
      if (frame.current === null) frame.current = requestAnimationFrame(measure);
    };

    measure();
    element.addEventListener('scroll', schedule, { passive: true });

    const observer = new ResizeObserver(schedule);
    observer.observe(element);

    return () => {
      element.removeEventListener('scroll', schedule);
      observer.disconnect();
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [ref]);

  return metrics;
};
